import { IconInfoCircle } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function requiredLabel(label: string) {
  return (
    <>
      <span aria-hidden="true" className="text-destructive-foreground">
        *
      </span>
      {label}
    </>
  );
}

export function TextField({
  name,
  label,
  value,
  onChange,
  type = "text",
  required = false,
  suffix,
  error,
}: {
  name: string;
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  type?: string;
  required?: boolean;
  suffix?: string;
  error?: string;
}) {
  return (
    <Field data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={`sb-${name}`}>{required ? requiredLabel(label) : label}</FieldLabel>
      {suffix ? (
        <InputGroup>
          <InputGroupInput
            id={`sb-${name}`}
            name={name}
            type={type}
            value={value}
            aria-invalid={error ? true : undefined}
            onChange={(event) => onChange?.(event.target.value)}
          />
          <InputGroupAddon align="inline-end">
            <InputGroupText>{suffix}</InputGroupText>
          </InputGroupAddon>
        </InputGroup>
      ) : (
        <Input
          id={`sb-${name}`}
          name={name}
          type={type}
          value={value}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange?.(event.target.value)}
        />
      )}
      {error ? <FieldError>{error}</FieldError> : null}
    </Field>
  );
}

export function DisabledSelectField({
  label,
  blocker,
  required = false,
}: {
  label: string;
  blocker: string;
  required?: boolean;
}) {
  const { t } = useTranslation("campaignCreation");
  const items = [{ value: null, label: t("sb.common.select") }];
  return (
    <Field data-disabled>
      <div className="flex items-center gap-1.5">
        <FieldLabel>{required ? requiredLabel(label) : label}</FieldLabel>
        <BlockerHint label={blocker} />
      </div>
      <Select items={items} value={null} disabled>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value={null}>{t("sb.common.select")}</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  );
}

export function SelectField({
  label,
  value,
  onValueChange,
  options,
  disabled = false,
  description,
  required = false,
  error,
}: {
  label: string;
  value: string | null;
  onValueChange: (value: string | null) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  description?: string;
  required?: boolean;
  error?: string;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <Field data-disabled={disabled || undefined} data-invalid={error ? true : undefined}>
      <FieldLabel>{required ? requiredLabel(label) : label}</FieldLabel>
      <Select
        items={[{ value: null, label: t("sb.common.select") }, ...options]}
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full" aria-invalid={error ? true : undefined}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value={null}>{t("sb.common.select")}</SelectItem>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </Field>
  );
}

export function SwitchField({
  checked,
  onCheckedChange,
  label,
  description,
  disabled = false,
  blocker,
}: {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label: string;
  description: string;
  disabled?: boolean;
  blocker?: string;
}) {
  return (
    <Field className="gap-2">
      <span className="flex items-center gap-1.5">
        <FieldLabel>{label}</FieldLabel>
        <BlockerHint label={blocker ?? description} />
      </span>
      <span className="flex items-center gap-3">
        <Switch
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-label={label}
          disabled={disabled}
        />
        <FieldDescription>{description}</FieldDescription>
      </span>
    </Field>
  );
}

function BlockerHint({ label }: { label: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          type="button"
          aria-label={label}
          className="text-muted-foreground hover:text-foreground"
        >
          <IconInfoCircle className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ChoiceRadioGroup({
  value,
  onValueChange,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <RadioGroup value={value} onValueChange={onValueChange} className="grid grid-cols-2 gap-4">
      {children}
    </RadioGroup>
  );
}

export function ChoiceRadioCard({
  value,
  label,
  description,
  children,
}: {
  value: string;
  label: string;
  description?: string;
  children?: ReactNode;
}) {
  const id = `sb-choice-${value}`;
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: The semantic radio remains the keyboard target; this wrapper only expands its pointer hit area.
    // biome-ignore lint/a11y/useKeyWithClickEvents: Keyboard selection is already provided by the nested RadioGroupItem and RadioGroup arrow-key behavior.
    <div
      data-slot="sb-choice-radio-card"
      className="cursor-pointer rounded-lg border border-border p-4 text-left transition-[border-color,box-shadow] has-[[data-slot=radio][data-checked]]:border-primary has-[[data-slot=radio][data-checked]]:shadow-sm"
      onClick={(event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (
          target?.closest(
            "a, button, input, select, textarea, label, [role=button], [role=combobox]",
          )
        ) {
          return;
        }
        document.getElementById(id)?.click();
      }}
    >
      <Field orientation="horizontal" className="items-start">
        <RadioGroupItem id={id} value={value} aria-label={label} />
        <span className="flex flex-col gap-1">
          <FieldLabel htmlFor={id}>{label}</FieldLabel>
          {description ? <FieldDescription>{description}</FieldDescription> : null}
        </span>
      </Field>
      {children}
    </div>
  );
}
